import { FC, useMemo, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Tooltip } from '../../../components/tooltip/index';
import { useSelected } from '../../../hooks/company/index';
import { useScreenSize } from '../../../hooks/index';
import { IssuedSharesFormula } from '../../../types/company.types';
import { Snapshot } from '../../../types/snapshot.types';
import { activeColors, fillColors, strokeColors } from '../../cap-table/graphs/index';
import { EmptyPositionGraph } from './empty-position-graph';

export type PositionGraphProps = {
  snapshot?: Snapshot;
  stakeholderId: string;
};

export const PositionGraph: FC<PositionGraphProps> = ({ stakeholderId, snapshot }) => {
  const [activeIndex, setActiveIndex] = useState<null | number>(null);

  const { selectedCompany } = useSelected();
  const { width } = useScreenSize();
  const isDesktop = width > 1120;
  const isMobile = width < 660;

  const isCapTableEmpty =
    !snapshot ||
    !snapshot?.stats.stakeholders.some(({ diluted, id }) => diluted && id === stakeholderId);

  const data = useMemo(() => {
    if (isCapTableEmpty)
      return [
        {
          id: '1',
          name: 'Michael Smith',
          issuedPercentage: 25,
        },
        {
          id: '2',
          name: 'Nicholas Johnson',
          issuedPercentage: 33,
        },
        {
          id: '3',
          name: 'Mark Rodriguez',
          issuedPercentage: 15,
        },
        {
          id: '',
          name: 'Others',
          issuedPercentage: 22,
        },
        {
          id: 'pool_id',
          name: 'Pool',
          issuedPercentage: 5,
        },
      ];

    const stakeholders = snapshot.stats.stakeholders;
    const totalIssued = snapshot.issuedShares;

    const calculateIssuedPercentage = (issuedShares: number) => {
      return (issuedShares * 100) / (totalIssued || 1);
    };

    const sortedStakeholders = [...stakeholders].sort(
      (a, b) => b.issuedPercentage - a.issuedPercentage,
    );

    const userIndex = sortedStakeholders.findIndex(({ id }) => id === stakeholderId);
    const userInTop4 = userIndex >= 0 && userIndex < 4;

    const formatStakeholders = (stakeholdersArray: typeof sortedStakeholders) =>
      stakeholdersArray.map(({ id, name, issued }) => ({
        id,
        name,
        issuedPercentage: calculateIssuedPercentage(issued),
      }));

    const getOthers = (remainingStakeholders: typeof sortedStakeholders) => {
      const othersTotalPercentage = remainingStakeholders.reduce(
        (total, { issued }) => total + issued,
        0,
      );
      return {
        id: '',
        name: 'Others',
        issuedPercentage: calculateIssuedPercentage(othersTotalPercentage),
      };
    };

    let formattedData = [];

    if (userInTop4) {
      const top4 = sortedStakeholders.slice(0, 4);
      const others = sortedStakeholders.slice(4);
      const isExistOthers = getOthers(others)?.issuedPercentage > 0;
      formattedData = isExistOthers
        ? [...formatStakeholders(top4), getOthers(others)]
        : formatStakeholders(top4);
    } else {
      const top3 = sortedStakeholders.slice(0, 3);
      const others = sortedStakeholders.filter((_, index) => index >= 3 && index !== userIndex);
      const isExistOthers = getOthers(others)?.issuedPercentage > 0;
      const userStakeholder = sortedStakeholders[userIndex];

      formattedData = isExistOthers
        ? [
            ...formatStakeholders(top3),
            {
              id: userStakeholder?.id,
              name: userStakeholder?.name,
              issuedPercentage: calculateIssuedPercentage(userStakeholder?.issued),
            },
            getOthers(others),
          ]
        : [
            ...formatStakeholders(top3),
            {
              id: userStakeholder?.id,
              name: userStakeholder?.name,
              issuedPercentage: calculateIssuedPercentage(userStakeholder?.issued),
            },
          ];
    }

    if (
      selectedCompany?.issuedSharesFormula === IssuedSharesFormula.ISSUED_SHARES_AND_AVAILABLE_POOL
    ) {
      const totalIssuedPercentage = formattedData.reduce(
        (total, { issuedPercentage }) => total + issuedPercentage,
        0,
      );

      formattedData.push({
        id: 'pool_id',
        name: 'Pool',
        issuedPercentage: 100 - totalIssuedPercentage,
      });
    }

    return formattedData.filter(({ issuedPercentage }) => issuedPercentage > 0);
  }, [
    isCapTableEmpty,
    selectedCompany?.issuedSharesFormula,
    snapshot?.issuedShares,
    snapshot?.stats.stakeholders,
    stakeholderId,
  ]);

  const handleMouseEnter = (index: number) => {
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveIndex(null);
  };

  return (
    <div
      className={twMerge(
        'flex w-[40%] min-w-[396px] flex-grow flex-col gap-3 rounded-lg bg-gray-50 p-1 pt-3',
        isMobile && 'min-w-[100px]',
      )}
    >
      <span className="pl-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
        <AppFormattedMessage id="YOUR_POSITION_ISSUED" />
      </span>
      <div
        className={twMerge(
          'relative flex w-full items-center justify-center gap-8 rounded-md bg-white px-7 py-9 shadow-sm',
          isMobile && 'flex-col px-3 py-8',
        )}
      >
        {isCapTableEmpty && (
          <span className="absolute z-1 text-center text-label-md font-medium text-gray-500">
            <AppFormattedMessage id="YOUR_POSITION_IN_CAP_TABLE_WILL_BE_HERE" />
          </span>
        )}
        <div className={twMerge('min-w-[180px]', isDesktop ? 'size-[240px]' : 'size-[280px]')}>
          {data.length > 0 ? (
            <ResponsiveContainer
              className={twMerge(
                isCapTableEmpty && 'pointer-events-none select-none opacity-50 blur-md',
              )}
              height="100%"
              width="100%"
            >
              <PieChart>
                <Pie
                  cx="50%"
                  cy="50%"
                  data={data}
                  dataKey="issuedPercentage"
                  innerRadius="50%"
                  isAnimationActive={false}
                  outerRadius="100%"
                >
                  {data.map((entry, index) => (
                    <Cell
                      fill={
                        activeIndex === index
                          ? activeColors[index % activeColors.length]
                          : fillColors[index % fillColors.length]
                      }
                      fillOpacity={1}
                      key={entry.name}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                      stroke={strokeColors[index % strokeColors.length]}
                      style={{
                        transition: 'all 0.5s ease, stroke 0.5s ease',
                      }}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyPositionGraph />
          )}
        </div>
        <div
          className={twMerge(
            'flex max-w-[216px] flex-wrap gap-x-4 gap-y-2 pt-2',
            isMobile && 'w-full justify-center',
            isCapTableEmpty && 'select-none opacity-50 blur-md',
          )}
        >
          {data
            .filter((item) => item.issuedPercentage > 0)
            .map((item, index) => (
              <div
                className="flex w-[100px] flex-col gap-x-4 gap-y-2 pb-2"
                key={`${item.name}_${index}`}
              >
                <div className="flex gap-1">
                  <span
                    className="truncate text-label-md font-[500] text-gray-600"
                    style={{
                      color: activeIndex !== null && activeIndex !== index ? '#98A2B3' : '#344054',
                      transition: 'color 0.5s ease',
                    }}
                  >
                    {item.name === 'Others' ? (
                      <AppFormattedMessage id="OTHERS" />
                    ) : (
                      <Tooltip childrenWrapperClassName="max-w-[100px]" content={item.name}>
                        {item.name}
                      </Tooltip>
                    )}
                  </span>
                  {item.id === stakeholderId && (
                    <span
                      className="text-label-md font-[500] text-gray-600"
                      style={{
                        color:
                          activeIndex !== null && activeIndex !== index ? '#98A2B3' : '#344054',
                        transition: 'color 0.5s ease',
                      }}
                    >
                      (<AppFormattedMessage id="YOU" />)
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <div
                    className="h-[5px] w-3 rounded"
                    style={{
                      backgroundColor:
                        activeIndex === index
                          ? activeColors[index % activeColors.length]
                          : fillColors[index % fillColors.length],
                      border:
                        activeIndex === index
                          ? `1px solid ${activeColors[index % activeColors.length]}`
                          : `1px solid ${fillColors[index % fillColors.length]}`,
                      transition: 'background-color 0.5s ease, border 0.5s ease',
                    }}
                  />
                  <span
                    className="whitespace-nowrap text-sm font-[450]"
                    style={{
                      color:
                        activeIndex !== null
                          ? activeIndex === index
                            ? activeColors[index % activeColors.length]
                            : '#98A2B3'
                          : '#344054',
                      transition: 'color 0.5s ease',
                    }}
                  >
                    {item.issuedPercentage.toFixed(2)} %
                  </span>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
